import React, { useState, useEffect } from 'react';
import {
  Box,
  Stat,
  StatLabel,
  StatNumber,
  Spinner,
  Image,
  SimpleGrid,
  Heading,
} from '@chakra-ui/react';

const StatsSlide = ({ backgroundImage, title }) => {
  const [stats, setStats] = useState({
    totalMembers: 0,
    minorsCount: 0,
    adultsCount: 0,
    totalDonations: 0,
  });
  const [eventsByCategory, setEventsByCategory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Category color mapping
  const categoryStyleMap = {
    GIOCO: { bg: '#1a8cff', color: 'white' },
    MAGIC: { bg: '#ff8c1a', color: 'white' },
    VARIANTE: { bg: '#b266ff', color: 'white' },
    EVENTO: { bg: '#4a5568', color: 'white' },
    YOUNG: { bg: '#87CEEB', color: 'white' },
    ARTE: { bg: '#e95995', color: 'white' },
    NATURA: { bg: '#3eb297', color: 'white' },
    GENERAL: { bg: '#ffcc00', color: 'black' },
  };

  const getCategoryStyles = (category) => {
    if (!category) return { bg: '#4a5568', color: 'white' };
    const macroKey = category.toUpperCase();
    return categoryStyleMap[macroKey] || { bg: '#4a5568', color: 'white' };
  };

  useEffect(() => {
    // Fetch statistiche
    fetch('/api/statistics')
      .then((res) => res.json())
      .then((data) => {
        setStats({
          totalMembers: data.totalMembers || 0,
          minorsCount: data.minorsCount || 0,
          adultsCount: data.adultsCount || 0,
          totalDonations: data.totalDonations || 0,
        });
      })
      .catch(() => {});

    // Fetch eventi disponibili e raggruppa per categoria
    // Usa l'endpoint completo con pageSize alto per ottenere tutti gli eventi futuri
    fetch('/api/eventi?past=false&page=1&pageSize=200')
      .then((res) => res.json())
      .then((data) => {
        const events = data.events || [];

        // Raggruppa eventi per categoria
        const categoryCounts = {};
        events.forEach((event) => {
          let category = event.category || 'GENERAL';

          // Estrai la macro categoria (es. "MAGIC – Draft" -> "MAGIC")
          // Le categorie possono essere nel formato "MACRO – Subcategoria"
          if (category.includes('–')) {
            category = category.split('–')[0].trim();
          } else if (category.includes('-')) {
            category = category.split('-')[0].trim();
          }

          const categoryKey = category.toUpperCase();
          categoryCounts[categoryKey] = (categoryCounts[categoryKey] || 0) + 1;
        });

        // Converti in array e filtra solo categorie con almeno un evento
        const eventsByCat = Object.entries(categoryCounts)
          .map(([category, count]) => {
            const styles = getCategoryStyles(category);
            return {
              category,
              count,
              styles,
            };
          })
          .filter((item) => item.count > 0);

        setEventsByCategory(eventsByCat);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Box
        height="100vh"
        width="100vw"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="black"
      >
        <Spinner size="xl" color="white" />
      </Box>
    );
  }

  return (
    <Box
      height="100vh"
      width="100vw"
      bg="black"
      position="relative"
      overflow="hidden"
    >
      {/* Immagine di sfondo ruotata */}
      {backgroundImage && (
        <Image
          src={
            typeof backgroundImage === 'string'
              ? backgroundImage
              : backgroundImage.url || backgroundImage
          }
          alt="Background"
          position="absolute"
          top="50%"
          left="50%"
          width="100vh"
          height="100vw"
          objectFit="cover"
          opacity={0.7}
          zIndex={0}
          transform="translate(-50%, -50%) rotate(-90deg)"
          transformOrigin="center"
          onError={() => {}}
        />
      )}

      {/* Overlay scuro per leggibilità */}
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        bg="blackAlpha.300"
        zIndex={1}
      />

      {/* Contenuto ruotato */}
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={8}
        transform="rotate(-90deg)"
        transformOrigin="center"
        zIndex={2}
      >
        {/* Container con proporzioni 9:16 per schermo verticale */}
        <Box
          width="90vh"
          height="calc(90vh * 16 / 9)"
          maxWidth="90vw"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="flex-start"
          pt={8}
          px={4}
        >
          {/* Titolo */}
          <Box mb={6} textAlign="center" zIndex={3}>
            <Heading
              fontSize="5xl"
              color="white"
              fontWeight="900"
              textShadow="2px 2px 4px rgba(0,0,0,0.8)"
              bg="blackAlpha.600"
              px={6}
              py={3}
              borderRadius={8}
            >
              {title || 'Le statistiche di CIL'}
            </Heading>
          </Box>

          {/* Primo terzo: Soci Totali e Soci Minori */}
          <Box
            height="33.33%"
            width="100%"
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            gap={8}
          >
            <Stat
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
            >
              <StatNumber
                fontSize="8xl"
                color="white"
                fontWeight="extrabold"
                mb={4}
              >
                {stats.totalMembers}
              </StatNumber>
              <StatLabel
                fontSize="xl"
                color="white"
                fontWeight="bold"
                bg="black"
                px={4}
                py={2}
                borderRadius={8}
              >
                Numero Soci Totali
              </StatLabel>
            </Stat>
            <Stat
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
            >
              <StatNumber
                fontSize="8xl"
                color="white"
                fontWeight="extrabold"
                mb={4}
              >
                {stats.minorsCount}
              </StatNumber>
              <StatLabel
                fontSize="xl"
                color="white"
                fontWeight="bold"
                bg="black"
                px={4}
                py={2}
                borderRadius={8}
              >
                Numero Soci Minori
              </StatLabel>
            </Stat>
          </Box>

          {/* Secondo terzo: Donazioni e Adulti */}
          <Box
            height="33.33%"
            width="100%"
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            gap={8}
          >
            <Stat
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
            >
              <StatNumber
                fontSize="8xl"
                color="white"
                fontWeight="extrabold"
                mb={4}
              >
                €
                {stats.totalDonations.toLocaleString('it-IT', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </StatNumber>
              <StatLabel
                fontSize="xl"
                color="white"
                fontWeight="bold"
                bg="black"
                px={4}
                py={2}
                borderRadius={8}
              >
                Totale Donazioni
              </StatLabel>
            </Stat>
            <Stat
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
            >
              <StatNumber
                fontSize="8xl"
                color="white"
                fontWeight="extrabold"
                mb={4}
              >
                {stats.adultsCount}
              </StatNumber>
              <StatLabel
                fontSize="xl"
                color="white"
                fontWeight="bold"
                bg="black"
                px={4}
                py={2}
                borderRadius={8}
              >
                Numero Soci Adulti
              </StatLabel>
            </Stat>
          </Box>

          {/* Terzo terzo: Eventi per categoria */}
          <Box
            height="33.33%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={4}
          >
            <SimpleGrid columns={2} spacing={4} width="100%">
              {eventsByCategory.map((item) => {
                const categoryStyles = getCategoryStyles(item.category);
                return (
                  <Box
                    key={item.category}
                    bg={categoryStyles.bg}
                    px={4}
                    py={3}
                    borderRadius={8}
                    width="100%"
                  >
                    <Stat
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      justifyContent="center"
                      gap={2}
                      width="100%"
                    >
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap={3}
                      >
                        <StatNumber
                          fontSize="5xl"
                          color="white"
                          fontWeight="extrabold"
                          m={0}
                        >
                          {item.count}
                        </StatNumber>
                        <StatLabel
                          fontSize="md"
                          color={categoryStyles.color}
                          fontWeight="bold"
                          m={0}
                          textAlign="left"
                          whiteSpace="pre-line"
                          textTransform="uppercase"
                        >
                          {/* Testo prima del nome categoria */}
                          Attività {item.count === 1 ? 'taggata' : 'taggate'}
                          {'\n'}
                          {item.category}
                        </StatLabel>
                      </Box>
                    </Stat>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StatsSlide;
